import type { TranslationKey } from '@/stores/localization.ts'

export const en: {
  [key in TranslationKey]: string | { zero?: string; one?: string; other: string }
} = {
  greeting: 'Hello',
  updateAvailable: 'New update available.',
  updateError: 'Error while updating!',
  errorBoundaryTitle: 'Whoops!',
  errorBoundaryDescription:
    'Sorry for that, that should not have happened. You can try again or reload the page.',
  errorBoundaryRetry: 'Try again',
  errorBoundaryReload: 'Reload',
  checkingForUpdates: 'Checking for updates …',
  noUpdateAvailable: 'Already up to date!',
  updateCheckFailed: 'Update failed due to unknown reasons.',
  noveltyPageHeader: 'Novelties',
  readButton: 'Read',
  collectedButton: 'Collected',
  addToCollectionButton: 'Add to collection',
  noItemsFound: 'Try different search terms or filters',
  noItemsFoundEmptyCustomList: 'Search for entries to add to this list now.',
  noItemsFoundHeaderMonthly: 'Found no novelties for this month.',
  noItemsFoundHeader: 'Nothing found',
  noItemsFoundWithFilter: 'Search Database',
  noItemsFoundResetFilter: 'Reset filters',
  noItemsFoundEmptyCollection: 'No entries yet',
  noItemsInCollection: 'Scan a barcode to add something to your collection!',
  volumePrefix: 'Volume',
  collectionPrefix: 'Bundle',
  collectionEntryLine: '%{prefix}%{number} (Vol. %{range}) · %{price} %{currency}',
  collectionEntryLineNoVolumes: '%{prefix}%{number} · %{price} %{currency}',
  collectionEntryLineVolumeCount: '%{prefix}%{number} (%{count} Vol.) · %{price} %{currency}',
  collectionItemTitleLine: '%{prefix} %{number} (Vol. %{range})',
  collectionItemTitleLineNoVolumes: '%{prefix} %{number}',
  collectionItemTitleLineVolumeCount: '%{prefix} %{number} (%{count} Vol.)',
  collectionItemTitleLineNoNumber: '%{prefix} (Vol. %{range})',
  collectionItemTitleLineNoVolumesNoNumber: '%{prefix}',
  collectionItemTitleLineVolumeCountNoNumber: '%{prefix} (%{count} Vol.)',
  releaseItemTitleLine: 'Volume %{number}',
  releaseItemTitleLineWithSuffix: 'Volume %{number} · %{volumeSuffix}',
  detailsListPostLine: '%{date} · %{price} %{currency}',
  collectionDetailEntryLine: '%{prefix} (Vol. %{range}) · %{price} %{currency} · %{date}',
  collectionDetailEntryLineNoVolumes: '%{prefix} · %{price} %{currency} · %{date}',
  collectionDetailEntryLineVolumeCount:
    '%{prefix} (%{count} Vol.) · %{price} %{currency} · %{date}',
  collectionRangeLabel: 'Volume %{range}',
  addedTitledVolumesToCollection: {
    one: '%{title} · Volume %{range} added to your collection',
    other: '%{title} · Volumes %{range} added to your collection',
  },
  collectionRangeLabelWithTitle: '%{title} Volume %{range}',
  slipCasePrefix: 'Slipcase',
  collectionEntryLineCompact: '%{prefix} · %{price} %{currency}',
  collectionEntryLineCompactNoPrice: '%{prefix}',
  prunedReleaseLabel: 'Release',
  listItemPrunedToast: '%{item} removed from %{list}.',
  emptySlipcase: 'Empty Slipcase',
  releaseEntryPriceLine: '%{prefix} %{number} · %{price} %{currency}',
  releaseEntryPriceLineWithSuffix: '%{prefix} %{number} · %{volumeSuffix} · %{price} %{currency}',
  releaseEntryLine: '%{prefix} %{number}',
  releaseEntryLineWithSuffix: '%{prefix} %{number} · %{volumeSuffix}',
  collectionEntryLineNoPrice: '%{prefix}%{number} (Vol. %{range})',
  collectionEntryLineNoVolumesNoPrice: '%{prefix}%{number}',
  collectionEntryLineVolumeCountNoPrice: '%{prefix}%{number} (%{count} Vol.)',
  collectionDetailEntryLineNoPrice: '%{prefix} (Vol. %{range}) · %{date}',
  collectionDetailEntryLineNoVolumesNoPrice: '%{prefix} · %{date}',
  collectionDetailEntryLineVolumeCountNoPrice: '%{prefix} (%{count} Vol.) · %{date}',
  detailsListPostLineNoPrice: '%{date}',
  releaseEntryNameLine: '%{name} · %{price} %{currency}',
  publisherLine: '%{publisher}',
  publisherAndDateLine: '%{publisher} · %{date}',
  infoExtraHeader: 'Extras',
  infoExtraFirstEditionHeader: 'Extras (first edition only)',
  infoDescriptionHeader: 'Description',
  infoPublisherDescriptionHeader: 'From the publisher',
  infoArtistHeader: {
    one: 'Artist',
    other: 'Artists',
  },
  infoAuthorHeader: {
    one: 'Author',
    other: 'Authors',
  },
  infoTranslatorHeader: {
    one: 'Translator',
    other: 'Translators',
  },
  releaseEntryVolumeAmountLine: {
    one: '1 Volume',
    other: '%{count} Volumes',
  },
  releaseEntryVolumeAmountLineWithType: {
    one: '1 Volume · %{type}',
    other: '%{count} Volumes · %{type}',
  },
  filterChipBarClearAll: 'Clear all',
  listStatsLine: 'Series: %{series} · Volumes: %{volumes}/%{released}',
  listSeriesLine: 'Series: %{series}',
  settingsPageLabel: 'Settings',
  languagesGerman: 'German',
  languagesEnglish: 'English',
  languagesSelect: 'Select language',
  bottomNavNovelties: 'Novelties',
  bottomNavMyLists: 'My Lists',
  bottomNavExploration: 'Explore',
  bottomNavSettings: 'Settings',
  bottomNavProfile: 'Profile',
  profilePageLabel: 'Profile',
  profileBiographyHeading: 'Biography',
  profileBiographyEmpty: 'No biography added yet.',
  profileMemberSince: 'Member since %{date}',
  profileEditNameLabel: 'Edit name',
  profileNameUpdateError: 'Could not update name.',
  statisticsPageLabel: 'Statistics',
  statisticsTabCollection: 'Collecting',
  statisticsTabReading: 'Reading',
  statisticsSectionGeneral: 'General',
  statisticsSectionSold: 'Sold',
  statisticsOwnedSeries: 'Series owned',
  statisticsOwnedVolumes: 'Volumes owned',
  statisticsNewValue: 'Retail value',
  statisticsSoldVolumes: 'Sold volumes',
  statisticsSoldValue: 'Sale proceeds',
  statisticsReadVolumes: 'Volumes read',
  statisticsReadLast30Days: 'Read (30 days)',
  statisticsAddedLast30Days: 'Added (30 days)',
  genericDropdownPlaceholder: 'Select',
  genericSelectModalTitle: 'Please select value',
  releaseEntryFilterHeader: 'Filter',
  releaseEntrySortHeader: 'Sort',
  confirmDropdownSelection: 'Confirm selection',
  releaseEntryFilterMonthSelectionLabel: 'Month',
  releaseEntryFilterMonthSelectionModalTitle: 'Select Month',
  releaseEntryFilterYearSelectionLabel: 'Year',
  releaseEntryFilterYearSelectionModalTitle: 'Select Year',
  releaseEntryFilterPublisherSelectionLabel: 'Publisher',
  releaseEntryFilterPublisherSelectionModalTitle: 'Select Publisher',
  releaseEntryFilterMediumSelectionLabel: 'Medium',
  releaseEntryFilterMediumSelectionModalTitle: 'Select Medium',
  releaseEntryFilterTagSelectionLabel: 'Tags',
  releaseEntryFilterCuratedListSelectionLabel: 'My Lists',
  releaseEntryFilterPromotedTagSelectionLabel: 'Special',
  releaseEntryFilterDemographySelectionLabel: 'Demographic',
  releaseEntryFilterPriceSelectionLabel: 'Price',
  releaseEntryFilterPlaceholderAll: 'All',
  releaseEntryFilterShowMore: 'Others',
  modalOkayButton: 'Okay',
  modalCancelButton: 'Cancel',
  monthJanuary: 'January',
  monthFebruary: 'February',
  monthMarch: 'March',
  monthApril: 'April',
  monthMay: 'May',
  monthJune: 'June',
  monthJuly: 'July',
  monthAugust: 'August',
  monthSeptember: 'September',
  monthOctober: 'October',
  monthNovember: 'November',
  monthDecember: 'December',
  sortReleaseDateLabel: 'Release Date',
  sortPublisherLabel: 'Publisher',
  sortTitleLabel: 'Title',
  sortPriceLabel: 'Price',
  sortCollectionChangedLabel: 'Last change',
  sortTotalVolumesLabel: 'Total Volumes (Original)',
  sortTotalReleasedVolumesLabel: 'Total Volumes (Germany)',
  sortCreationDateLabel: 'Date Added',
  sortManualOrder: 'Manual order',
  noDateSetForRelease: 'No date available',
  registerButtonLabel: 'Register',
  loginButtonLabel: 'Login',
  socialLoginButtonLabel: 'Continue with %{provider}',
  authPageSsoSeparatorLabel: 'or',
  passwordRepeatMatchError: 'Passwords do not match',
  authEmptyFieldError: 'Please fill out all fields',
  authEmailPlaceholder: 'E-Mail',
  authNicknamePlaceholder: 'Nickname',
  authPasswordPlaceholder: 'Password',
  authPasswordRepeatPlaceholder: 'Repeat Password',
  authResponseEmailError: 'Invalid email address',
  authResponseLoginEmailPasswordError: 'Invalid email or password',
  authResponsePasswordError:
    'Password must be at least 8 characters long, contain at least one lowercase letter and one number',
  authResponseErrorUnknown: 'An unknown error occurred, please contact us!',
  authResponseSignUpNotAllowed: 'No sign up allowed at the moment',
  authResponseSignInNotAllowed: 'No sign in allowed at the moment',
  authSsoTimeout:
    '%{provider} took too long to respond. Please check your connection and try again.',
  authSsoFailed: 'Failed to sign in with %{provider}. Please try again or contact us.',
  authOfflineLoginError: 'Signing in requires an internet connection.',
  errorNoInternet: 'No internet connection',
  errorServerUnavailable: 'Server unavailable, please tell us about it!',
  errorRequestFailed: 'Request failed, please tell us about it!',
  emailAlreadyVerified: 'Email is already verified',
  emailVerificationRequiredMessage: 'Please verify your email address.',
  verificationSendMessage: 'We sent you an email with a verification link.',
  verificationAlreadyDoneMessage: 'Email is already verified, you can login now.',
  toLoginButtonLabel: 'To Login',
  comingSoon: 'Coming soon',
  verifyEmailUnknownError:
    'An unknown error occurred while verifying your email address, please contact us.',
  verifyEmailInvalidTokenError: 'Invalid verification toke.',
  verifyEmailSuccess: 'Email successfully verified!',
  verifyEmailLoading: 'Verifying email …',
  backToLoginLinkText: 'Back to Login',
  forgotPasswordLinkText: 'Forgot password?',
  loginHeading: 'Login',
  registerHeading: 'Register',
  resetPasswordHeading: 'Reset\npassword',
  loginNoAccountRegisterLink: 'No account? Register now.',
  registerHaveAccountLoginLink: 'Already have an account? Log in.',
  continueButtonLabel: 'Continue',
  showPasswordLabel: 'Show password',
  hidePasswordLabel: 'Hide password',
  ssoLoginButtonLabel: 'Log in with %{provider}',
  ssoRegisterButtonLabel: 'Register with %{provider}',
  sendResetPasswordButtonLabel: 'Reset password',
  resetPasswordEmailSent: 'If an account exists with this email, a reset link has been sent.',
  resetPasswordCooldown: 'Available again in %{seconds}s',
  newPasswordPlaceholder: 'New password',
  confirmPasswordPlaceholder: 'Confirm password',
  resetPasswordButtonLabel: 'Reset password',
  resetPasswordSuccess: 'Your password has been reset successfully.',
  resetPasswordInvalidTokenError: 'This reset link is invalid or has expired.',
  resetPasswordMismatchError: 'Passwords do not match.',
  resetPasswordWeakError: 'Password is too weak.',
  bulkEditNewCollectionTitle: 'Add to collection',
  bulkEditExistingCollectionTitle: 'Edit',
  bulkEditUnknownError: 'An unknown error occurred',
  bulkEditUnavailableForBundle: 'Cannot use bulk edit on this entry.',
  bundleSpanAddTitleBundle: 'Add bundle?',
  bundleSpanAddTitleSlipcase: 'Add slipcase?',
  bundleSpanAddConfirm: 'Add',
  bundleSpanAddIntroBundle:
    'Bulk edit is unfortunately not available for this bundle.\nIf you add this bundle, the following releases will be added to your collection:',
  bundleSpanAddIntroSlipcase:
    'Bulk edit is unfortunately not available for this slipcase.\nIf you add this slipcase, the following releases will be added to your collection:',
  bundleSpanAddFooterBundle: 'You can remove the bundle from any of the releases at any time.',
  bundleSpanAddFooterSlipcase: 'You can remove the slipcase from any of the releases at any time.',
  collectionSyncError: 'Failed to save collection changes. Please try again.',
  sharedCollectionSiblingsUpdated: 'Also updated in: %{titles}',
  sharedCollectionSiblingsAdded: 'Also added in: %{titles}',
  sharedCollectionSiblingsRemoved: 'Also removed from: %{titles}',
  releaseSelectionTableOwnedCell: 'Collected',
  releaseSelectionTableReadCell: 'Read',
  collectionModalBulkEditCancel: 'Cancel',
  collectionModalBulkEditCreateNew: 'Add to collection',
  collectionModalBulkEditUpdate: 'Save',
  myListsHeader: 'My Lists',
  explorationHeader: 'Database',
  explorationExploreTab: 'Recommendations',
  explorationLicensesTab: 'New Licenses',
  runningReleasesCollectingTabTitle: 'Collecting',
  runningReleasesReadingTabTitle: 'Reading',
  doneReleasesCollectingTabTitle: 'Collected',
  doneReleasesReadingTabTitle: 'Read',
  pausedReleaseCollectingTabTitle: 'Paused',
  pausedReleasesReadingTabTitle: 'Paused',
  planningReleasesTabTitle: 'Planned',
  abortedReleasesCollectingTabTitle: 'Aborted',
  removedReleasesCollectingTabTitle: 'Removed',
  notPlannedReleasesTabTitle: 'Not Planned',
  abortedReleasesReadingTabTitle: 'Aborted',
  databaseUpcomingTitle: 'Upcoming Titles',
  databasePopularTitle: 'Popular Titles',
  databaseRecommendedTitle: 'Recommended Titles',
  databaseSearchTitle: 'Search',
  sortPopularityLabel: 'Popularity',
  sortRecommendationLabel: 'Recommendation',
  releaseEntryFilterStartDatePickerLabel: 'Released after',
  releaseEntryFilterEndDatePickerLabel: 'Released before',
  releaseEntryFilterDatePickerPlaceholder: 'Select date',
  releaseEntryFilterDateClear: 'Clear',
  releaseEntryFilterDateSectionLabel: 'Date',
  releaseEntryFilterStartDateChipLabel: 'Appears from:',
  releaseEntryFilterEndDateChipLabel: 'Appears until:',
  releaseEntryFilterRelationTypeLabel: 'Type',
  relationTypeVolume: 'Volume',
  relationTypeCollection: 'Slipcase/Bundle',
  relationTypeExtra: 'Extra',
  searchPlaceholder: 'Search …',
  searchPlaceholderNovelty: 'Search novelties …',
  searchPlaceholderDatabase: 'Search database …',
  scanYourMangaTitle: 'Scan a barcode',
  bulkEditPlannedLabel: 'Planned',
  bulkModalVolumeLabel: 'Volume %{number}',
  bulkModalVolumeLabelWithSuffix: 'Volume %{number} · %{volumeSuffix}',
  authPageComingSoonTitle:
    'The web app is currently in development. Please be patient and use the app for now.',
  webAccessRestrictedMessage:
    'Sorry! This function of atsume is currently only available for supporters and testers.',
  bulkModalNotReleasedWarning: 'You have selected volumes that are technically not released yet!',
  deleteCollectionButton: 'Delete',
  collectionModalBulkEditAcceptDelete: 'Delete',
  confirmReleaseDeleteHeader: 'Delete entry?',
  collectionModalDeleteWarningText:
    'You are about to delete this entry permanently. This action cannot be undone.',
  settingsAppSettingsLabel: 'General',
  settingsAppSettingsDesignLabel: 'Design',
  settingsAppSettingsDesignAutomatic: 'Automatic',
  light: 'Bright',
  dark: 'Dark',
  settingsAppSettingsStartPageLabel: 'Start Page',
  settingsAppSettingsLanguageLabel: 'Language',
  settingsAppNotifications: 'Notifications',
  settingsAccountSettingsLabel: 'Account',
  settingsAccountSettingsLogoutLabel: 'Logout',
  settingsTestAreaLabel: 'Test Area',
  settingsListViewLabel: 'View',
  listViewModeGallery: 'Gallery',
  listViewModeList: 'List',
  listViewModeCompact: 'Compact',
  settingsInfoLabel: 'Information',
  settingsOfflineDataLabel: 'Offline data',
  offlineGateTitle: 'You are offline',
  offlineGateMessage: 'Offline use is a premium feature. Reconnect or support us to continue.',
  offlineGatePremiumCta: 'Support atsume',
  settingsOfflineLastSyncedLabel: 'Last synced',
  settingsOfflineStorageLabel: 'Storage',
  settingsOfflineSyncing: 'Syncing…',
  settingsOfflineNeverSynced: 'Never',
  settingsOfflineModeLabel: 'Offline mode',
  settingsOfflineModeHint:
    'Downloads the catalog to your device so the app works without internet.',
  settingsOfflineModeEnable: 'Enable offline mode',
  settingsOfflineConsentTitle: 'Download data for offline mode?',
  settingsOfflineConsentTitleProgress: 'Enabling offline mode.',
  settingsOfflineConsentMessage:
    'The catalog is downloaded to your device so the app works without internet. The download runs in the background over WiFi. You can change this anytime in settings.',
  settingsOfflineConsentOffline: 'Data',
  settingsOfflineConsentOfflineCovers: 'Data + covers',
  settingsOfflineConsentAccept: 'Download',
  settingsOfflineConsentDecline: 'Later',
  settingsOfflineDownloadingToast: 'Downloading offline data.',
  settingsOfflineWaitingWifi: 'Waiting for WiFi',
  settingsOfflineWaitingWifiHint: 'The offline download starts automatically once you are on WiFi.',
  settingsOfflineForceDownload: 'Download now over mobile data',
  offlineDataVanished: 'Offline data is gone — you are back online.',
  settingsOfflineForceLabel: 'Force offline mode',
  settingsOfflineUnavailable: 'Unavailable',
  settingsOfflinePhaseDownloading: 'Downloading',
  settingsOfflinePhaseExtracting: 'Extracting',
  settingsOfflinePhaseApplying: 'Saving',
  settingsOfflinePhaseMigrating: 'Migrating',
  settingsOfflinePhaseUpdating: 'Updating',
  settingsOfflinePhaseImages: 'Downloading covers',
  settingsOfflineImagesLabel: 'Offline covers',
  settingsOfflineImagesEnable: 'Enable offline pictures',
  settingsOfflineImagesQuality: 'Picture quality',
  settingsOfflineImagesQualityLow: 'Low',
  settingsOfflineImagesQualityMedium: 'Medium',
  settingsOfflineImagesQualityHigh: 'High',
  settingsOfflineImagesStorageLabel: 'Data',
  settingsInfoVersionLabel: 'Version',
  optionsModalCancel: 'Cancel',
  optionsModalSave: 'Save',
  optionsModalOkay: 'Okay',
  emailPlaceholder: 'E-Mail',
  passwordPlaceholder: 'Password',
  passwordRepeatPlaceholder: 'Repeat Password',
  namePlaceholder: 'Name',
  optionsLoading: 'Loading options',
  serverUnavailableMessage: 'The server is currently unavailable. Please wait a moment …',
  serverUnavailableDiscordCta: 'Ask on Discord',
  collectionStatus: 'Collection status',
  readingStatus: 'Read status',
  collectStatusPlanned: 'Planned',
  collectStatusCollecting: 'Collecting',
  collectStatusDone: 'Done',
  collectStatusPaused: 'Paused',
  collectStatusAborted: 'Aborted',
  collectStatusSold: 'Removed',
  readStatusNotPlanned: 'Not Planned',
  readStatusPaused: 'Paused',
  readStatusPlanned: 'Planned',
  readStatusReading: 'Reading',
  readStatusDone: 'Done',
  readStatusAborted: 'Aborted',
  selectVolumeVersion: 'Select volume version',
  selectVolumeOverviewVolumeLabel: 'Volume %{number}',
  defaultVolumeSuffix: 'Standard-Edition',
  defaultVolumeSuffixAbr: 'SE',
  ambiguousVolumeIncreaseDetected: 'Please choose a Version for Volume %{number}',
  tmpAddTestLabel: 'Test ad',
  tmpTestSubscriptionLabel: 'Test Abo',
  dangerZoneLabel: 'Danger zone',
  deleteAccountSettingsLabel: 'Delete Account',
  deleteAccountLabel: 'Delete',
  confirmDeleteHeader: 'Delete account?',
  confirmDeleteLabel: 'Are you sure you want to delete your account? This action cannot be undone.',
  deletingAccount: 'Deleting account',
  pleaseEnterText: 'Please enter the text %{text} to confirm.',
  'atsume_ad_free:ad-free-1': 'No ads (Monthly payment) - %{price}',
  'atsume_ad_free:ad-free-2': 'No ads (Yearly payment) - %{price}',
  adConsentModalHeader: 'Ad Time',
  adConsentModalBodyText:
    'When you watch an ad, we give you a token that allows you to skip the next ad. You can also use atsume ad-free by subscribing to a plan.',
  adConsentModalAmountOfTokens: {
    zero: 'You have no tokens',
    one: 'You have 1 token',
    other: 'You have %{count} tokens',
  },
  adConsentModalCtaBuyAdFree: 'Buy ad-free',
  adConsentModalCtaSkipAd: 'Skip (-%{count})',
  adConsentModalCtaUpdatePreferencesToAutoSkipAd: 'Update preferences to auto-skip ads',
  adConsentModalCtaShowShortAd: 'Short Ad (+%{count})',
  adConsentModalCtaShowLongAd: 'Long Ad (+%{count})',
  admobErrorUseAdToken: 'Error using ad tokens',
  subscriptionModalHeader: 'Upgrade your experience',
  subscriptionModalSubHeader: 'Choose a plan that works for you.',
  monthly: 'Monthly',
  yearly: 'Yearly',
  oneTimeYearly: 'For one Jahr',
  bestValue: 'Best Value',
  perMonth: 'per month',
  perYear: 'yer year',
  youWantToSupportAtsume: 'You want to support atsume? Become a Patron and get a lot of benefits!',
  billedAsYearlyOneTime: 'Only %{amount} for one Year!',
  billedAsYearly: 'For only %{amount} per Year!',
  billedAsMonthly: 'For only %{amount} per Month!',
  becomeSupporterNowButtonLabel: 'Become a Patron now',
  youWantToSupportUs: 'You want to support us?',
  becomePatronButtonLabel: 'Support the app now!',
  thankYouForSupporting: 'Thank you for your support!',
  youAreAPatron: 'You are a Patron',
  patron: 'Patron',
  saveMoneyHeader: 'Save Money',
  saveMoneyDescription: 'Get two full months for free when you sign up for an annual subscription!',
  noPriceIncreaseHeader: 'No Price Increase',
  noPriceIncreaseDescription: 'All future premium features at a lower price – forever!',
  noAdsHeader: 'Bye-bye, Ads',
  noAdsDescription:
    'Manga tracking without annoying third-party ads that will be integrated in the future.',
  discordRoleHeader: 'Exclusive Discord Role',
  discordRoleDescription:
    'Receive supporter status and highlight your wishes in an exclusive channel.',
  supportDevelopmentHeader: 'Support Development',
  supportDevelopmentDescription:
    'With your help we keep the database up to date and work on new features!',
  choosePlan: 'Choose Plan',
  privacyPolicy: 'Privacy Policy',
  termsOfService: 'Terms of Service',
  subscribeAgreePrefix: 'By subscribing, you agree to our',
  subscribeAgreeAnd: 'and',
  subscribeAgreeSuffix: '.',
  restorePurchases: 'Restore Purchases',
  notNow: 'Not Now',
  purchaseError: 'Purchase error: %{error}',
  loadProductsError: 'Could not load subscription options. Please try again later.',
  admobNoFillLucky: 'No ads available right now. :)',
  addedVolumesToCollection: {
    one: 'Volume %{range} added to collection',
    other: 'Volumes %{range} added to collection',
  },
  activePlan: 'Active plan',
  showConsentFormLabel: 'Show consent form',
  releaseEntryNoDate: 'TBA',
  releaseEntryFilterCollectionLabel: 'In my Collections',
  releaseEntryFilterCollectionSectionLabel: 'Collection',
  releaseEntryFilterNovelties: 'Novelties',
  releaseEntryFilterUnowned: 'Unowned',
  releaseEntryFilterUnread: 'Unread',
  releaseEntryFilterNoDuplicates: 'No Duplicates',
  releaseEntryFilterHidePlanned: 'No Planned',
  releaseEntryFilterHideCollecting: 'No Ongoing',
  releaseEntryFilterHidePaused: 'No Paused',
  featureCategoryFilter: 'Features',
  collectionFilterAllDone: 'Current',
  collectionFilterMissing: 'Missing entries',
  collectionFilterGaps: 'Gaps',
  sortByLabel: 'Sort by',
  collectionFilter: 'Collection',
  filterCollectionStatusLabel: 'By Collection Status',
  filterReadStatusLabel: 'By Read Status',
  missingIsbnHeaderTitle: 'ISBN not found!',
  missingIsbnBody:
    'We could not find the book with ISBN %{isbn} in our database. Please tell us which title is missing. You will be able to add the title later without having to scan it again.',
  missingIsbnHeaderTitleUpdate: 'ISBN already reported',
  missingIsbnBodyUpdate:
    'You have already reported ISBN %{isbn}. We are working on adding this title. You can update your description here.',
  missingIsbnNotGermanWarning:
    'This ISBN does not look like a German ISBN. Unfortunately we currently only focus on German releases. Are you sure this is a German release?',
  missingIsbnGermanConfirm: 'I confirm that this is a German release.',
  scanError: 'Error while scanning barcode, please try again.',
  scanNotSupported: 'Barcode scanning is not supported on this device.',
  cameraPermissionDenied: 'Camera permission is required to scan barcodes.',
  cameraPermissionDeniedOpenSettings: 'Camera permission was denied. Please enable it in Settings.',
  couldNotSaveMissingIsbn: 'Could not save missing ISBN, please try again later.',
  releaseTypeManga: 'Manga',
  releaseTypeManhwa: 'Manhwa',
  releaseTypeManhua: 'Manhua',
  releaseTypeArtbook: 'Artbook',
  releaseTypeLnovel: 'Light Novel',
  releaseTypeAnimeComic: 'Anime Comics',
  releaseTypeMagazine: 'Magazine',
  releaseStatusRunning: 'Running',
  releaseStatusDone: 'Completed',
  releaseStatusPaused: 'Paused',
  releaseStatusCanceled: 'Canceled',
  releaseTabInformation: 'Information',
  releaseTabVolumes: 'Volumes',
  releaseTabCollections: 'Slipcases/Bundles',
  releaseEditButton: 'Edit',
  releaseShareButton: 'Share',
  addToListButton: 'Add to list',
  addToListSheetTitleSeries: 'Add series to list',
  addToListSheetTitleVolume: 'Add volume to list',
  addToListCreateNew: 'Create new list',
  addToListEntryCount: {
    one: '1 entry',
    other: '%{count} entries',
  },
  addToListError: 'Could not update list.',
  addToListForSeries: 'This list is for series',
  addToListForVolumes: 'This list is for volumes',
  sharePopupTitle: 'Share',
  shareTwitter: 'X',
  shareFacebook: 'Facebook',
  shareWhatsapp: 'WhatsApp',
  shareTelegram: 'Telegram',
  shareReddit: 'Reddit',
  shareEmail: 'Email',
  shareCopyLink: 'Copy Link',
  shareLinkCopied: 'Link copied to clipboard',
  listsCollectionTabLabel: 'Collection',
  listsReadTabLabel: 'Reading',
  listsCustomTabLabel: 'Custom',
  customListsEmptyTitle: 'No lists yet',
  customListsEmptyBody: 'Create a personalized one now!',
  customListsLoadFailedTitle: 'Lists could not be loaded',
  customListsLoadFailedBody: 'Please check your connection and try again.',
  customListsLoadFailedRetry: 'Try again',
  listCreateHeader: 'New List',
  listEditHeader: 'Edit list',
  listFormNameLabel: 'Name',
  listFormDescriptionLabel: 'Description',
  listFormSettingsLabel: 'Settings',
  listFormPrefilledItemLabel: 'Will be added',
  listFormTypeLockedError: 'Type cannot be changed while the list has entries.',
  listFormTypeLabel: 'Type',
  listFormTypeReleaseEntry: 'Series',
  listFormTypeNovelty: 'Volumes',
  listFormPublicLabel: 'Public',
  listFormRemoveWhenCollectedLabel: 'Remove when collected',
  listFormRemoveWhenReadLabel: 'Remove when read',
  listFormShowTotalNumberLabel: 'Show total number',
  listFormShowTotalValueLabel: 'Show total value',
  listMetricNoveltyCount: 'Items: %{count}',
  listMetricReleaseEntryCount: 'Series: %{series} · Volumes: %{volumes}',
  listMetricValue: '%{value} €',
  listFormCustomTagLabel: 'Custom tag',
  listFormCustomTagNameLabel: 'Tag name',
  listFormSaveLabel: 'Save',
  listFormNameRequiredError: 'Please enter a name.',
  listFormCustomTagRequiredError: 'Please enter a tag name.',
  listFormDeleteLabel: 'Delete',
  listFormDeleteError: 'Could not delete the list.',
  listDeleteConfirmHeader: 'Delete list',
  listDeleteConfirmBody:
    'This list and all associated entries will be permanently deleted. This cannot be undone.',
  listFormSaveError: 'Could not save the list.',
  createListAction: 'Create list',
  customListLimitReachedToast: 'Free tier list limit reached. You can support us to get more!',
  editListAction: 'Edit list',
  listNotFoundTitle: 'List not found',
  listNotFoundBody: 'This list does not exist or has been deleted.',
  listNotFoundAction: 'Back to my lists',
  databaseEmptyConfigs: 'No database lists are currently configured.',
  artbook: 'Artbook',
  lnovel: 'Light Novel',
  animeComic: 'Anime Comics',
  magazine: 'Magazine',
  manga: 'Manga',
  manhua: 'Manhua',
  manhwa: 'Manhwa',
  isbnLabel: 'ISBN',
  mediumType: 'Medium',
  pagesLabel: 'Pages',
  formatLabel: 'Format',
  sizesLabel: 'Sizes',
  originalDataLabel: 'Original data',
  titleLabel: 'Title',
  publisherLabel: 'Publisher',
  magazineLabel: 'Magazine',
  statusLabel: 'Status',
  volumesLabel: 'Volumes',
  originCountryLabel: 'Origin',
  empty: '',
  canceled: 'Canceled',
  completed: 'Completed',
  running: 'Running',
  statusChangeRead: 'Set read status from %{oldStatus} to %{newStatus}.',
  statusChangeCollection: 'Set collection status from %{oldStatus} to %{newStatus}.',
  de: 'Germany',
  fr: 'France',
  zh: 'China',
  tw: 'Taiwan',
  jp: 'Japan',
  it: 'Italy',
  es: 'Spain',
  eu: 'Europe',
  gb: 'United Kingdom',
  ko: 'South Korea',
  us: 'United States',
  testAreaImportLabel: 'Import collection',
  importTitle: 'Import collection',
  importStepSourceTitle: 'Choose source',
  importStepUploadTitle: 'Upload file',
  importStepMappingTitle: 'Map fields',
  importStepMatchingTitle: 'Matching',
  importStepPreviewTitle: 'Preview',
  importStepCommittingTitle: 'Importing',
  importStepResultTitle: 'Result',
  importSourceBookshelf: 'Bookshelf (CSV)',
  importSourceReado: 'READO (JSON)',
  importSourceCustom: 'atsume CSV',
  importInstructionsBookshelf:
    'Open Bookshelf → Settings → Import and Export → Export → Select csv as file type and all shelves → Tap Export → Upload the exported file here.',
  importInstructionsReado:
    'Click the "Copy Request Text" button below → Open Reado app → Tap Profile at the bottom → Tap the burger menu in the top right → Tap Settings → Tap "Customer Support Chat" → Paste the text from clipboard and request your data → Once you receive the JSON file, upload it here → The JSON file contains sensitive data. The app only reads ISBN and list structures and does not transmit any of it to our servers.',
  importReadoCopyButton: 'Copy Request Text',
  importReadoCopied: 'Copied!',
  importReadoRequestText:
    'Hello, I would like to request a copy of my personal data in accordance with GDPR Article 15. Please send me the export of my READO account data in a machine-readable format. Thank you.',
  importInstructionsAtsume:
    'Create a CSV with headers: isbn, list_name, collection_status, read_status, is_collected, is_read. Other columns are ignored.',
  importDownloadExample: 'Download Example',
  importPickFileButton: 'Pick file',
  importFilePickedLabel: '%{name} · %{rows} rows',
  importFileTooLargeWarning: 'Over 5000 rows — import may take a while.',
  importFileParseError: 'Could not read the file.',
  importMappingExplanation: 'Assign each value to a collection / read status.',
  importMappingIgnore: 'Ignore',
  importMappingCollectionStatus: 'Collection status',
  importMappingReadStatus: 'Read status',
  importMatchingProgress: 'Looking up %{done} of %{total} ISBNs …',
  importPreviewTabNew: 'New (%{count})',
  importPreviewTabConflicts: 'Conflicts (%{count})',
  importPreviewTabUnmatched: 'Unmatched (%{count})',
  importPreviewSearchPlaceholder: 'Search by title',
  importPreviewVolumesLine: '%{count} volumes',
  importPreviewIncludeRow: 'Include',
  importConflictSkip: 'Skip',
  importConflictOverwrite: 'Overwrite',
  importNextButton: 'Next',
  importBackButton: 'Back',
  importStartButton: 'Start import',
  importCommittingProgress: 'Importing %{done} of %{total} …',
  importResultTitle: 'Import complete',
  importResultCreated: 'Newly added',
  importResultOverwritten: 'Overwritten',
  importResultSkipped: 'Skipped',
  importResultUnmatched: 'Unmatched ISBNs',
  importResultDownloadReport: 'Download Report',
  importResultDoneButton: 'Done',
  importEmptyState: 'No entries in this list.',
  importNoSourceSelected: 'Pick a source first.',
  importMissingIsbnNote: 'Imported from %{source}',
  importBucketWishlist: 'Wishlist',
  importBucketUnlabeled: 'Unlabeled',
  importPreviewCollections: '%{count} collections',
  importPreviewEmptyNew: 'No new entries.',
  importPreviewEmptyConflicts: 'No conflicts.',
  importPreviewEmptyUnmatched: 'All ISBNs matched.',
  notificationsTitle: 'Notifications',
  notificationsEmpty: 'No notifications',
  notificationSettingsTitle: 'Notification Settings',
  notificationSettingsDisabledHint:
    'Enable notifications to receive updates about your collection.',
  notificationSettingsEnableButton: 'Enable Notifications',
  notificationSettingsDisableButton: 'Disable Notifications',
  notificationSettingsEnabled: 'Notifications enabled',
  notificationSettingsDisabled: 'Notifications disabled',
  notificationSettingsDenied: 'Notifications denied',
  notificationSettingsCollectionHeader: 'Collection',
  notificationSettingsNewReleasesHeader: 'New Releases',
  notificationSettingsAddedReleaseDate: 'New release date for volumes in collection',
  notificationSettingsChangedReleaseDate: 'Changed release date for volumes in collection',
  notificationSettingsItemAvailable: 'Volume from collection releases',
  notificationSettingsAlternativeVersion: 'Alternative version of a volume releases',
  notificationSettingsScannerHeader: 'Scanner',
  notificationSettingsScannedItemAvailable: 'Reported ISBN is now in the catalog',
  notificationSettingsAtsumeHeader: 'atsume',
  notificationSettingsAtsumeRecommendations: 'Recommendations',
  notificationSettingsFromPublishers: 'New releases from selected publishers',
  notificationSettingsFromAuthors: 'New releases from selected authors/artists',
  notificationSettingsAddPublisher: 'Add publisher',
  notificationSettingsAddAuthor: 'Add author/artist',
  notificationSettingsSearchPublisher: 'Search publisher',
  notificationSettingsSearchAuthor: 'Search author/artist',
  notificationSettingsSaved: 'Settings saved',
  notificationSettingsSaveError: 'Error saving settings',
}
